import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export interface BroadcastBannerData {
  id: string;
  message: string;
  severity: 'info' | 'warning' | 'success' | 'error';
  ctaLabel?: string | null;
  ctaLink?: string | null;
  iconName?: string | null;
  isDismissible: boolean;
  version: number;
  createdAt: string;
}

export function useActiveBroadcastBanners() {
  return useQuery({
    queryKey: ['broadcast-banners', 'active'],
    queryFn: () => apiClient.get<{ success: boolean; data: BroadcastBannerData[] }>('/broadcast-banners/active'),
    staleTime: 60_000,
    refetchInterval: 120_000,
    select: (res: any) => (res?.data || []) as BroadcastBannerData[],
  });
}

export function useBroadcastBanners() {
  return useQuery({
    queryKey: ['broadcast-banners'],
    queryFn: () => apiClient.get('/broadcast-banners'),
  });
}

export function useCreateBroadcastBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiClient.post('/broadcast-banners', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broadcast-banners'] });
      queryClient.invalidateQueries({ queryKey: ['broadcast-banners', 'active'] });
    },
  });
}

export function useUpdateBroadcastBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => apiClient.put(`/broadcast-banners/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broadcast-banners'] });
      queryClient.invalidateQueries({ queryKey: ['broadcast-banners', 'active'] });
    },
  });
}

export function useUpdateBroadcastBannerStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiClient.patch(`/broadcast-banners/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broadcast-banners'] });
      queryClient.invalidateQueries({ queryKey: ['broadcast-banners', 'active'] });
    },
  });
}

export function useDeleteBroadcastBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/broadcast-banners/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['broadcast-banners'] });
      queryClient.invalidateQueries({ queryKey: ['broadcast-banners', 'active'] });
    },
  });
}
